import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { useAppSelector, useActionless } from 'redux/hooks'

import { SET_ACTIVE_STATE } from 'redux/active/actions/types'

import { useShowTagRecsMenu } from 'hooks/showTagRecsMenu'

import MultiSelectControls from 'components/deckPage/modals/MultiSelectControls'
import HighlightedCardControls from 'components/deckPage/modals/HighlightedCardControls'
import FloatingMenuWrapper from 'components/misc/FloatingMenu/Wrapper'
import TagModal from 'components/deckPage/tagRecommender/TagModal'

import styles from './floatingMenu.module.scss'

type MenuType = 'multiSelect' | 'highlighted' | 'tagRecommendations'

const FloatingMenu = () => {
  const router = useRouter()
  const preSelectedUids = `${router.query?.selectedUids || ''}`
  const preSelectedOracleUids = `${router.query?.selectedOracleUids || ''}`

  const [menuItems, setMenuItems] = useState<MenuType[]>([])
  const [visibleMenuItems, setVisibleMenuItems] = useState<MenuType[]>([])

  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const highlightedCards = useAppSelector(state => state.active.highlightedCards)
  const multiSelectedIds = useAppSelector(state => state.active.multiSelectedIds)
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const showTagReminderMenu = useShowTagRecsMenu()

  useEffect(() => {
    if (!preSelectedUids && !preSelectedOracleUids) return

    const preSelectedCardUids = preSelectedUids
      .split(',')
      .reduce((acc, uid) => ({ ...acc, [uid]: true }), {} as Record<string, boolean>)

    const preSelectedOracleCardUids = preSelectedOracleUids
      .split(',')
      .reduce((acc, oracleUid) => ({ ...acc, [oracleUid]: true }), {} as Record<string, boolean>)

    const preSelectedCardIds = Object.values(cardMap)
      .filter(card => preSelectedCardUids[card.uid] || preSelectedOracleCardUids[card.oracleCardUid])
      .reduce((acc, card) => ({ ...acc, [card.id]: true }), {} as Record<string, boolean>)

    setActiveState({ multiSelectedIds: { ...preSelectedCardIds } })
  }, [])

  useEffect(() => {
    const actualMenuState: Record<MenuType, boolean> = {
      multiSelect: Object.keys(multiSelectedIds).length > 0,
      highlighted: Object.keys(highlightedCards).length > 0,
      tagRecommendations: showTagReminderMenu,
    }

    for (const menuType of Object.keys(actualMenuState)) {
      const type = menuType as MenuType

      const menuShouldBeOpen = actualMenuState[type]
      const menuIsInMenuArray = menuItems.indexOf(type) > -1

      // Enter
      if (menuShouldBeOpen && !menuIsInMenuArray) {
        setMenuItems([...menuItems, type])
        setTimeout(() => setVisibleMenuItems([...visibleMenuItems, type]), 250)
      }

      // Leaving
      if (!menuShouldBeOpen && menuIsInMenuArray) {
        setVisibleMenuItems(visibleMenuItems.filter(item => item !== type))
        setTimeout(() => setMenuItems(menuItems.filter(item => item !== type)), 250)
      }
    }
  }, [highlightedCards, multiSelectedIds, showTagReminderMenu])

  return (
    <div className={styles.container}>
      {menuItems.map(menuType => {
        const visabilityIndex = visibleMenuItems.findIndex(item => item === menuType)

        // Transitions offscreen when visabilityIndex is -1
        return (
          <FloatingMenuWrapper
            // Reversing the list to bump items up as new items come into the DOM
            level={visabilityIndex !== -1 ? visibleMenuItems.length - 1 - visabilityIndex : -1}
            nutralBorder={menuType === 'highlighted'}
            key={menuType}>
            {menuType === 'multiSelect' && <MultiSelectControls />}
            {menuType === 'highlighted' && <HighlightedCardControls />}
            {menuType === 'tagRecommendations' && <TagModal />}
          </FloatingMenuWrapper>
        )
      })}
    </div>
  )
}

export default FloatingMenu
